import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback. Receives the error + a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Class component error boundary — catches render errors in the subtree
 * and shows a fallback UI instead of crashing the whole app.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <RiskyComponent />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={(err, reset) => <div onClick={reset}>{err.message}</div>}>
 *     <RiskyComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ErrorBoundary] Uncaught error:",
      error,
      info.componentStack,
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) return fallback(error, this.reset);
      const raw = String(error?.message ?? "");
      // find apollo error link if present
      const apolloMatch = raw.match(
        /https?:\/\/go\.apollo\.dev\/c\/err#[^\s)\]\"]+/,
      );
      const apolloLink = apolloMatch ? apolloMatch[0] : null;
      const preview =
        raw.length > 240 ? raw.slice(0, 240).trimEnd() + "…" : raw;

      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-900/30 dark:bg-red-950/20">
          <p className="text-sm font-medium text-red-700 dark:text-red-400">
            Something went wrong
          </p>
          <div className="max-w-lg text-xs text-red-600 dark:text-red-500">
            <div className="mb-2 break-words text-left">{preview}</div>
            {apolloLink && (
              <div className="mb-2 text-left">
                <a
                  href={apolloLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-indigo-600 underline"
                >
                  View full Apollo error details
                </a>
              </div>
            )}
            <details className="text-left text-[12px] text-red-700">
              <summary className="mb-1 cursor-pointer">Show full error</summary>
              <pre className="whitespace-pre-wrap break-words text-xs text-red-600 dark:text-red-400">
                {raw}
              </pre>
            </details>
          </div>
          <button
            onClick={this.reset}
            className="rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400"
          >
            Try again
          </button>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
